import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast, Toaster } from 'react-hot-toast';
import { apiClient, exchangeBrowserLoginToken } from '../services/api';
import { useUserStore } from '../store/userStore';
import * as Sentry from '@sentry/react';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useUserStore();

  const handleTokenExchange = useCallback(async (token: string) => {
    setLoading(true);
    try {
      const response = await exchangeBrowserLoginToken(token);

      if (response.ok && response.token && response.me && response.properties) {
        // Initialize API client with token
        apiClient.createApi(response.token);

        // Login with token, account, and properties
        login(
          response.token,
          response.me,
          response.properties,
          {
            appVersion: 'standalone',
            platform: 'web',
            arch: 'web',
          }
        );

        // Reinitialize API client with both token and cookies
        apiClient.createApi(response.token, true);

        // Remove token from URL
        navigate('/', { replace: true });

        toast.success(t('login.success'), {
          position: 'top-center',
          duration: 3000,
        });
      } else {
        throw new Error(response.error || 'Token exchange failed');
      }
    } catch (error) {
      console.error('Token exchange error:', error);
      Sentry.captureException(error);

      // Remove token from URL even on error
      navigate('/login', { replace: true });

      const msg =
        error instanceof Error && error.message === 'cloud_disabled'
          ? t('login.errorOrgDisabled')
          : t('login.errorTokenExpired');

      toast.error(msg, {
        position: 'top-center',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }, [login, navigate, t]);

  // Handle browser login token exchange
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      handleTokenExchange(token);
    }
  }, [searchParams, handleTokenExchange]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error(t('login.errorMissingFields'));
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.login(email, password);

      if (response.ok && response.token && response.me) {
        // Get user properties/state
        try {
          // Initialize API client with token
          apiClient.createApi(response.token);

          // Get user state which includes properties
          const stateResponse = await apiClient.getState();

          if (stateResponse.ok && stateResponse.properties) {
            // Login with token, account, and properties
            login(
              response.token,
              response.me,
              stateResponse.properties,
              {
                appVersion: 'standalone',
                platform: 'web',
                arch: 'web',
              }
            );

            // Cookie is set by server via Set-Cookie header
            // The login() function in userStore will also set it client-side as backup
            // This ensures the cookie persists even if server cookie has issues

            // Reinitialize API client with both token (for Authorization header) and cookies
            apiClient.createApi(response.token, true);

            toast.success(t('login.success'));
            navigate('/');
          } else {
            throw new Error('Failed to get user state');
          }
        } catch (error) {
          console.error('Error getting user state:', error);
          Sentry.captureException(error);
          toast.error(t('login.errorInitSession'));
        }
      } else {
        // Map server error codes to user-friendly messages
        let errorMsg = t('login.errorInvalidCredentials');
        if (response.error) {
          if (response.error === 'invalid_credentials') {
            errorMsg = t('login.errorInvalidCredentials');
          } else if (response.error === 'cloud_disabled') {
            errorMsg = t('login.errorOrgDisabled');
          } else {
            errorMsg = response.error;
          }
        }
        toast.error(errorMsg);
        console.error('Login failed:', response);
      }
    } catch (error) {
      console.error('Login error:', error);
      Sentry.captureException(error);
      toast.error(t('login.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-sidebar p-4">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
      <div className="flex justify-center mb-4">
        <img
          src="https://folge.me/images/logo@2x.png"
          alt="Folge"
          className="h-20 w-auto"
        />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">{t('login.title')}</CardTitle>
          <CardDescription>
            {loading && searchParams.get('token')
              ? t('login.loggingIn')
              : t('login.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('login.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('login.password')}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t('login.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={loading || !email || !password}
            >
              {loading ? t('login.submitting') : t('login.submit')}
            </Button>
            <p className="text-center text-sm text-muted-foreground mt-3">
              <Link to="/reset-password" className="underline hover:text-foreground">
                {t('login.forgotPassword')}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
      <div className="mt-4">
        <LanguageSwitcher />
      </div>
    </div>
  );
};

export default LoginPage;
